import { usersApi } from '@/api'
import { authKeys } from '@/lib/queryKeys'
import { isPlatformUser } from '@/stores/auth'
import type { ApiError, LoginCredentials } from '@/stores/auth'
import { redirectToLoginIf401 } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'

export const useCurrentUser = () => {
	const setUser = useAuthStore(state => state.setUser)
	const setLoading = useAuthStore(state => state.setLoading)
	const setError = useAuthStore(state => state.setError)
	const user = useAuthStore(state => state.user)
	const currentTenant = useAuthStore(state => state.currentTenant)

	// Disable query on login page to prevent 401 redirect loop
	const isLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login'

	// Prevent fetching if user is an owner but not a member of the current tenant (prevents 403)
	const canFetch = useMemo(() => {
		if (isLoginPage) {
			return false
		}
		// If no user (initial load) or no tenant selected, safe to fetch
		if (!user || !currentTenant) {
			return true
		}

		// If Platform User checking specific tenant
		if (isPlatformUser(user)) {
			// If user is NOT a member of the current tenant, DO NOT FETCH
			if (!user.tenants?.includes(currentTenant)) {
				return false
			}
		}
		return true
	}, [isLoginPage, currentTenant, user])

	const query = useQuery({
		queryKey: [...authKeys.currentUser(), currentTenant],
		// Bind the /me tenant to the query key's tenant, not window.location. The
		// interceptor's URL-derived tenant lags during redirects/org switches and
		// would fetch a role:null user for the wrong (or no) tenant.
		queryFn: () => usersApi.getCurrentUser(currentTenant || undefined),
		enabled: canFetch,

		retry: (failureCount, error: ApiError) => {
			if (error?.status === 401) {
				return false
			}
			return failureCount < 3
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		refetchOnWindowFocus: true,
	})

	useEffect(() => {
		if (query.isSuccess && query.data) {
			setUser(query.data)
			setLoading(false)
			setError(null)
		} else if (query.isError) {
			if (redirectToLoginIf401(query.error, useAuthStore.getState().clearAuthState)) {
				return
			}
			setUser(null)
			setLoading(false)
			setError(query.error?.message || 'Failed to fetch user')
		}
	}, [query.isSuccess, query.isError, query.data, query.error, setUser, setLoading, setError])

	return query
}

export const useInitializeAuth = () => {
	const isAuthenticated = useAuthStore(state => state.isAuthenticated)

	const userQuery = useCurrentUser()

	return useMemo(
		() => ({
			isLoading: userQuery.isLoading,
			isError: userQuery.isError,
			error: userQuery.error,
			isAuthenticated,
			user: userQuery.data,
		}),
		[userQuery.isLoading, userQuery.isError, userQuery.error, userQuery.data, isAuthenticated],
	)
}

export const useLogin = () => {
	const login = useAuthStore(state => state.login)
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (credentials: LoginCredentials) => login(credentials),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: authKeys.currentUser() })
		},
		onError: (error: ApiError) => {
			console.error('Login failed:', error)
		},
	})
}
